// BADGE-KATALÓGUS — a rendszer MINDEN jelvényének egyetlen forrás-igazsága: fogalom (`kind`) × érték → tónus
// (a `Badge` `variant`-ja) + opcionális vezető ikon. A `DomainBadge` ezt rendereli, és két szabályt kényszerít
// ki, amit korábban komponensenként kellett újra betartani:
//   1. a felirat MINDIG kisbetűs (a design-szabály: a badge ÉRTÉK, nem mondat-kezdet) — EGY kivétellel: a
//      SAJÁT NEVEK (rendszer- és termék-nevek: HotelCMS, MailWizz, NAV, …) az eredeti írásmódjukban maradnak,
//      mert név, nem tárgy vagy általános megnevezés (user-döntés; l. `badgeLabelParts`),
//   2. azonos fogalom → azonos jel, bárhol jelenik meg (lista, részlet-panel, drilldown, modál).
//
// MIÉRT KATALÓGUS (és nem N kis badge-komponens): a tónus/ikon-térképek öt-hat fájlban éltek, és ahol egy
// fogalom két helyen kellett (pl. a hozzárendelés hatóköre a hozzárendelés-listán ÉS a bevétel-előrejelzés
// drilldownjában), ott a második hely SAJÁT térképet kapott — más színnel, más betűmérettel, ikon nélkül
// (user-lelet). Iparági minta (GitHub Primer `Label`/`StateLabel`, Atlassian `Lozenge`, Shopify
// Polaris `Badge`): egy atom + egy zárt, típusos státusz/kategória-térkép, a hívó csak a nyers értéket adja.
//
// A KORÁBBI névre szóló komponensek (`ScopeBadge`, `InvoiceStatusBadge`, `IdentityStatusBadge`, …) MEGMARADNAK
// vékony burkolóként — a hívók API-ja nem változik, a térkép viszont mind innen jön.
//
// A CÍMKÉT a hívó adja (i18n-agnosztikus komponens, mint a többi shared badge) — a katalógus a JELET írja le,
// nem a szót. Ismeretlen érték → semleges (`muted`) tónus + pötty, a nyers felirattal: sosem törik.
// IKON-CSALÁD: az ÁLLAPOT-jelek (életciklus, identitás- és számla-státusz) TELÍTETT (solid) ikont visznek —
// kicsiben (12 px) a kitöltött forma olvasható; az OSZTÁLYOZÓ jelek (hatókör, gyakoriság, megállapodás,
// üzenet-hatókör, részvétel) a rendszer alap vonalas (outline) családját, mint a navigáció.
// EGY KIMONDOTT KIVÉTEL: a `productUpdateType` és a `productUpdateVisibility` osztályozó jel, MÉGIS telített
// ikont visz. Ez a kettő a fejlesztés-KÁRTYÁN EGYMÁS MELLETT, PÁRBAN jelenik meg a cím alatt
// („mi ez" + „kinek szól"), és egy solid+outline páros ott egymáshoz képest hibásnak látszott. A `type` már a
// modul megépítésekor solid volt — a szabály és a gyakorlat eddig NÉMÁN csúszott el; ez a bekezdés zárja le.
import {
  ArrowPathIcon,
  ArrowTurnDownRightIcon,
  BanknotesIcon,
  BarsArrowUpIcon,
  BoltIcon,
  BriefcaseIcon,
  BuildingLibraryIcon,
  BuildingOffice2Icon,
  BuildingStorefrontIcon,
  CakeIcon,
  CalculatorIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ClockIcon,
  CodeBracketIcon,
  ComputerDesktopIcon,
  DocumentArrowUpIcon,
  EqualsIcon,
  EyeIcon,
  ForwardIcon,
  HomeModernIcon,
  LightBulbIcon,
  MapIcon,
  MegaphoneIcon,
  MinusCircleIcon,
  NoSymbolIcon,
  PauseCircleIcon,
  PencilSquareIcon,
  PercentBadgeIcon,
  PlusCircleIcon,
  QuestionMarkCircleIcon,
  ReceiptPercentIcon,
  RocketLaunchIcon,
  ScissorsIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  Square2StackIcon,
  Squares2X2Icon,
  StarIcon,
  SunIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  ArchiveBoxIcon as ArchiveBoxSolidIcon,
  ArrowDownTrayIcon as ArrowDownTraySolidIcon,
  ArrowPathIcon as ArrowPathSolidIcon,
  ArrowTrendingDownIcon as ArrowTrendingDownSolidIcon,
  ArrowUturnLeftIcon as ArrowUturnLeftSolidIcon,
  BanknotesIcon as BanknotesSolidIcon,
  BoltIcon as BoltSolidIcon,
  BugAntIcon as BugAntSolidIcon,
  CheckCircleIcon as CheckCircleSolidIcon,
  ClockIcon as ClockSolidIcon,
  CreditCardIcon as CreditCardSolidIcon,
  CubeIcon as CubeSolidIcon,
  DocumentIcon as DocumentSolidIcon,
  EnvelopeIcon as EnvelopeSolidIcon,
  EnvelopeOpenIcon as EnvelopeOpenSolidIcon,
  ExclamationTriangleIcon as ExclamationTriangleSolidIcon,
  GlobeAltIcon as GlobeAltSolidIcon,
  LifebuoyIcon as LifebuoySolidIcon,
  LockClosedIcon as LockClosedSolidIcon,
  MegaphoneIcon as MegaphoneSolidIcon,
  MinusCircleIcon as MinusCircleSolidIcon,
  PaperAirplaneIcon as PaperAirplaneSolidIcon,
  PauseIcon as PauseSolidIcon,
  PencilSquareIcon as PencilSquareSolidIcon,
  PlayIcon as PlaySolidIcon,
  ShieldCheckIcon as ShieldCheckSolidIcon,
  ShoppingCartIcon as ShoppingCartSolidIcon,
  SparklesIcon as SparklesSolidIcon,
  StrikethroughIcon as StrikethroughSolidIcon,
  Squares2X2Icon as Squares2X2SolidIcon,
  TagIcon as TagSolidIcon,
  WrenchIcon as WrenchSolidIcon,
  WrenchScrewdriverIcon as WrenchScrewdriverSolidIcon,
  XCircleIcon as XCircleSolidIcon,
} from '@heroicons/react/24/solid';
import type { ComponentType, HTMLAttributes, SVGProps } from 'react';

import { Badge, type BadgeProps } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

type IconType = ComponentType<SVGProps<SVGSVGElement>>;

/** Egy érték JELE: tónus + opcionális vezető ikon (ikon nélkül a felirat egyedül áll — nincs pötty). */
export interface BadgeVisual {
  variant: NonNullable<BadgeProps['variant']>;
  icon?: IconType;
}

// Rövidítések, hogy a térkép egy pillantással olvasható maradjon.
const v = (variant: BadgeVisual['variant'], icon?: IconType): BadgeVisual =>
  icon ? { variant, icon } : { variant };

/**
 * A katalógus. Új fogalom = új kulcs ITT (és a doksi `foundations/components.md` badge-táblájában), nem egy
 * új `Badge variant=…` a hívóban.
 */
export const BADGE_CATALOG = {
  /** A hozzárendelés / számla HATÓKÖRE — semleges tónus, az IKON hordozza a különbséget (nav-ikonokkal egyező). */
  scope: {
    client: v('default', BriefcaseIcon),
    account: v('default', BuildingOffice2Icon),
  },
  /** Számlázási GYAKORISÁG — ismétlődő: kék + körkörös nyíl; eseti: borostyán + nyugta (a legacy színe). */
  frequency: {
    monthly: v('sky', ArrowPathIcon),
    quarterly: v('sky', ArrowPathIcon),
    semi_annual: v('sky', ArrowPathIcon),
    annual: v('sky', ArrowPathIcon),
    one_time: v('warning', ReceiptPercentIcon),
  },
  /** Számlázás TÍPUSA (katalógus-csomag): ugyanaz a két jel, mint a gyakoriság két ága. */
  billingType: {
    recurring: v('sky', ArrowPathIcon),
    one_time: v('warning', ReceiptPercentIcon),
  },
  /** Fizetési MEGÁLLAPODÁS — az előre fizetett a kiemelt (brand: a pénz már megvan), az utólagos semleges. */
  agreement: {
    prepaid: v('brand', BanknotesIcon),
    postpaid: v('muted', BanknotesIcon),
  },
  /** Szolgáltatás SZERKEZETE a katalógusban: egyszerű (kocka, brand — mint a termék) / csomag (rács, violet). */
  serviceStructure: {
    simple: v('brand', CubeSolidIcon),
    package: v('violet', Squares2X2SolidIcon),
  },
  /** ÁRAZÁSI modell — osztályozás, nem állapot: semleges, ikon nélkül. */
  pricingType: {
    // fix díj = egy összeg (=) · sávos = lépcsőzetes sávok · alapdíj + sávos = alapdíj PLUSZ sáv · jutalék = százalék
    // (NEM a százalékos nyugta: az a Számlák menü ikonja)
    flat: v('secondary', EqualsIcon),
    tiered: v('secondary', BarsArrowUpIcon),
    base_tiered: v('secondary', PlusCircleIcon),
    commission: v('secondary', PercentBadgeIcon),
  },
  /** ÉLETCIKLUS-státusz (ügyfél · fiók · hozzárendelés) — azonos jelentés, azonos jel. */
  lifecycle: {
    active: v('success', CheckCircleSolidIcon),
    pending: v('warning', PauseSolidIcon),
    inactive: v('danger', XCircleSolidIcon),
  },
  /** Fiók-DOMAIN állapota (a fiók „Domainek" szekciója): él · beállítás alatt · már nem használt. */
  domainStatus: {
    active: v('success', CheckCircleSolidIcon),
    setup: v('warning', WrenchScrewdriverSolidIcon),
    inactive: v('muted', PauseSolidIcon),
  },
  /** Identitás-státusz (munkatárs / partner / gépi fiók). */
  identityStatus: {
    active: v('success', CheckCircleSolidIcon),
    suspended: v('warning', PauseSolidIcon),
    archived: v('muted', ArchiveBoxSolidIcon),
  },
  /**
   * A számla-GENERÁLÁS kihagyás-oka (`billing/invoice-generation-skips` napló). Az érték egy tényleges
   * probléma-állapot (miért nem lett belőle számla), ezért — az `invoiceStatus`/`ticketStatus` mintájára —
   * SOLID ikont visz, tónusa a SÚLYOSSÁGOT jelzi: `danger` = adat/konfiguráció hiányzik (a hozzárendelést
   * javítani kell), `warning` = időszakos/technikai (a következő futás magától pótolhatja), `muted` =
   * NEM hiba (`already_invoiced` — normál idempotencia).
   */
  generationSkipReason: {
    invalid_period: v('danger', ExclamationTriangleSolidIcon),
    already_invoiced: v('muted', CheckCircleSolidIcon),
    no_usage_source: v('danger', ExclamationTriangleSolidIcon),
    no_usage_data: v('warning', ExclamationTriangleSolidIcon),
    no_rate: v('danger', ExclamationTriangleSolidIcon),
    no_price: v('danger', ExclamationTriangleSolidIcon),
    zero: v('warning', ExclamationTriangleSolidIcon),
    eu_tax_number_required: v('danger', ExclamationTriangleSolidIcon),
    write_failed: v('danger', XCircleSolidIcon),
    rate_limited: v('warning', ClockSolidIcon),
    // A KÉSŐBB felvett okok — ezek eddig HIÁNYOZTAK a katalógusból, és szürke „ismeretlen érték" pöttyel álltak
    // (a felirat le volt fordítva, a jelvény mégis hibásnak látszott).
    issuer_missing: v('danger', ExclamationTriangleSolidIcon),
    currency_mismatch: v('danger', ExclamationTriangleSolidIcon),
    // a kapcsoló- és státusz-okok LEHETNEK szándékosak (nem adat-hiba) — figyelmeztetés, nem hiba
    auto_billing_off: v('warning', PauseSolidIcon),
    assignment_not_active: v('warning', PauseSolidIcon),
    owner_inactive: v('warning', PauseSolidIcon),
    // a kézi törlés ZÁRA: emberi döntés, nem hiba — a lakat a feloldható zárat jelöli
    manually_removed: v('muted', LockClosedSolidIcon),
    // a „nincs használati adat" pontosított okai: a lezárásra váró adat időszakos (a forrás pótolja) → warning;
    // a kikapcsolt ág és a hiányzó azonosító beállítás-hiba → danger (ember nélkül nem javul)
    usage_not_final: v('warning', ClockSolidIcon),
    usage_source_disabled: v('danger', PauseSolidIcon),
    // összevont minimum ágankénti hozzárendelésen: a modellt kell váltani (Direkt + AK) — beállítás-hiba
    usage_combined_minimum: v('danger', ArrowPathSolidIcon),
    missing_usage_ref: v('danger', ExclamationTriangleSolidIcon),
  },
  /**
   * A HASZNÁLATI SOR MEGJEGYZÉSE (a használati adatok lap „Megjegyzés" oszlopa): lesz-e belőle számla-tétel.
   * `success` = ebből számlázunk · `muted` = tájékoztató, NEM hiány (a másik változatból számlázunk · a
   * szolgáltatás lezárult / később indul · nem igénybe vett, 0-s mérés · fiók nélküli 0-s mérés) · `warning` =
   * van mért érték, de nincs rá szolgáltatás / inaktív a szolgáltatás vagy a tulajdonos / még nincs lezárva ·
   * `danger` = beállítás-hiba (mért értékhez nincs fiók az azonosítóval / a forrásban kikapcsolva). A
   * nyilvántartásos hozzárendelés `muted` (szándékos, nem hiány); a MÁSIK azonosítóra kötött `warning` (a kötést
   * kell ellenőrizni).
   */
  usageBillingNote: {
    billed: v('success', CheckCircleSolidIcon),
    billed_inactive: v('warning', PauseSolidIcon),
    other_variant: v('muted', ArrowPathSolidIcon),
    tracking: v('muted', DocumentSolidIcon),
    bound_elsewhere: v('warning', ArrowPathSolidIcon),
    ended: v('muted', ClockSolidIcon),
    not_started: v('muted', ClockSolidIcon),
    no_assignment: v('warning', ExclamationTriangleSolidIcon),
    unused: v('muted', MinusCircleSolidIcon),
    no_account: v('danger', ExclamationTriangleSolidIcon),
    no_account_unused: v('muted', MinusCircleSolidIcon),
    not_final: v('warning', ClockSolidIcon),
    disabled: v('danger', PauseSolidIcon),
    combined_minimum: v('danger', ArrowPathSolidIcon),
  },
  /** A kihagyás-napló sorának ÁLLAPOTA: nyitott (a legutóbbi futás is kihagyta) / lezárt (egy későbbi futás
   *  már számlázta). */
  generationSkipState: {
    open: v('warning', ClockSolidIcon),
    resolved: v('success', CheckCircleSolidIcon),
  },
  /** Számla-státusz (a Számlázz.hu-életciklus). */
  invoiceStatus: {
    draft: v('muted', DocumentSolidIcon),
    issued: v('warning', PaperAirplaneSolidIcon),
    paid: v('success', CheckCircleSolidIcon),
    overdue: v('danger', ExclamationTriangleSolidIcon),
    cancelled: v('muted', XCircleSolidIcon),
    storno: v('danger', ArrowUturnLeftSolidIcon),
  },
  /**
   * A partner-felület TÉTEL-JELÖLŐI (tájékoztató, nem állapot — a `success` a sikert, a `warning` a teendőt
   * jelenti, ezek egyike sem): közös számlázás (kék, két négyzet) · használat szerint (szürke, oszlop) ·
   * becslés (lila, számológép) · arányosított (szürke, olló). Az `info` tónus ebben a témában ZÖLD, ezért
   * a tájékoztatás kékje a `sky`.
   */
  portalBilling: {
    shared: v('sky', Square2StackIcon),
    usage: v('muted', ChartBarIcon),
    estimate: v('violet', CalculatorIcon),
    prorated: v('muted', ScissorsIcon),
  },
  /** A partner havi számlázásának ALAPJA: tény (számla) · terv (még nincs számla) · vegyes. */
  billingBasis: {
    actual: v('success', CheckCircleSolidIcon),
    plan: v('info', ClockIcon),
    mixed: v('warning', ArrowPathIcon),
  },
  /**
   * A BIZONYLAT FAJTÁJA (user-döntés): típus + kézbesítés egy jelvényben (`@atlas/shared` `invoiceDocKind`) —
   * e-számla (boríték) · papír alapú számla és díjbekérő (papír) · sztornó (piros, áthúzás).
   */
  invoiceDoc: {
    e_invoice: v('info', EnvelopeSolidIcon),
    paper_invoice: v('info', DocumentSolidIcon),
    proforma: v('violet', DocumentSolidIcon),
    storno: v('danger', StrikethroughSolidIcon),
  },
  /** Üzenet HATÓKÖRE a partner-portálon (körlevél · a fiókjának · személyesen). */
  messageScope: {
    broadcast: v('outline', MegaphoneIcon),
    account: v('muted', BuildingOffice2Icon),
    contact: v('brand', UserIcon),
  },
  /** Megbeszélés-részvétel válasza — a partner-portálon ÉS a belső résztvevő-panelen ugyanaz a jel. */
  rsvp: {
    pending: v('warning', QuestionMarkCircleIcon),
    confirmed: v('success', CheckCircleIcon),
    declined: v('muted', XMarkIcon),
  },
  /** Hozzárendelés TÍPUSA: számlázás (nyugta) / nyilvántartás (szem). */
  assignmentType: {
    billing: v('info', ReceiptPercentIcon),
    tracking: v('muted', EyeIcon),
  },
  /**
   * A hozzárendelés LE VAN ZÁRVA (van záró dátuma) — ugyanaz a lakat, ami a sor-akciót is jelöli.
   *
   * MIÉRT KELL KÜLÖN JELZÉS: a lezárás eddig csak a sor-akció lakatján látszott, a SOR maga ugyanúgy nézett
   * ki, mint egy élő (user-lelet: „azt sem tudom, sikeresen lezárja-e"). Élesben **591 lezárt sor** van,
   * ebből 385 nyilvántartás-típusú — ezek mind élőnek látszottak a listán.
   */
  assignmentClosed: {
    closed: v('muted', LockClosedSolidIcon),
  },
  /** Jegy típusa (a legacy szín-kódja tokenre fordítva). */
  ticketType: {
    support: v('secondary', LifebuoySolidIcon),
    bug: v('danger', BugAntSolidIcon),
    feature_request: v('violet', SparklesSolidIcon),
    billing: v('brand', BanknotesSolidIcon),
    other: v('muted', TagSolidIcon),
  },
  /**
   * Jegy OLVASATLAN-állapota a belső listán (a hívó utolsó megnyitása óta). A külső válasz UGYANAZ a jel, mint a
   * portál `newReply`-ja; a belső jegyzet a szál belső-jegyzet tónusát (warning) és lakatját kapja. A `none` nem
   * jelenik meg (nincs jelvény).
   */
  ticketUnread: {
    new_ticket: v('info', SparklesSolidIcon),
    external: v('brand', ChatBubbleLeftRightIcon),
    internal: v('warning', LockClosedSolidIcon),
  },
  /** Jegy státusza. */
  ticketStatus: {
    open: v('info', EnvelopeOpenSolidIcon),
    in_progress: v('warning', ArrowPathSolidIcon),
    resolved: v('success', CheckCircleSolidIcon),
    closed: v('muted', LockClosedSolidIcon),
    reopened: v('danger', ArrowUturnLeftSolidIcon),
  },
  /** Kinél van a sor egy jegyen (partner-portál): nálunk (brand) / a partner válaszára várunk (warning). */
  ticketTurn: {
    us: v('brand', ChatBubbleLeftRightIcon),
    partner: v('warning', ClockIcon),
  },
  /** Üzenet státusza (piszkozat · időzített · elküldve). */
  messageStatus: {
    draft: v('muted', DocumentSolidIcon),
    scheduled: v('info', ClockSolidIcon),
    sent: v('success', PaperAirplaneSolidIcon),
  },
  /** Eszköz életútja (igényléstől a kivezetésig). */
  assetStatus: {
    requested: v('info', EnvelopeSolidIcon),
    rejected: v('danger', XCircleSolidIcon),
    approved: v('violet', CheckCircleSolidIcon),
    being_procured: v('warning', ShoppingCartSolidIcon),
    purchased: v('warning', CreditCardSolidIcon),
    received: v('brand', ArrowDownTraySolidIcon),
    spare: v('secondary', ArchiveBoxSolidIcon),
    in_use: v('success', CheckCircleSolidIcon),
    retired: v('muted', ArrowTrendingDownSolidIcon),
  },
  /** Eszköz kategóriája. */
  assetCategory: {
    hardware: v('secondary', ComputerDesktopIcon),
    software: v('violet', Squares2X2Icon),
  },
  /** Bankkártya érvényessége (lista ÉS ügyfél-detail ugyanazzal a jellel). */
  paymentCardStatus: {
    valid: v('success', CheckCircleSolidIcon),
    expiring: v('warning', ClockSolidIcon),
    expired: v('danger', XCircleSolidIcon),
  },
  /** Fejlesztés (product update) típusa. */
  productUpdateType: {
    feature: v('violet', SparklesSolidIcon),
    improvement: v('brand', WrenchScrewdriverSolidIcon),
    fix: v('danger', WrenchSolidIcon),
    security: v('warning', ShieldCheckSolidIcon),
    performance: v('secondary', BoltSolidIcon),
  },
  /**
   * Fejlesztés fázisa — OSZTÁLYOZÓ jel (keretes tónus, vonalas ikon-család). Az IKON a board oszlop-fejléceit
   * szolgálja ki: ott a fázis nem egy cella értéke a sok közül, hanem maga a HELY, ahová a kártya kerül —
   * egy pillantással olvasható jel kell hozzá (a korábbi, táblás felületen a „Fázis" oszlopfejléc nevezte meg
   * a dimenziót, ezért volt szándékosan ikon nélküli).
   *
   * A jel-választás a fázis JELENTÉSÉT írja le, nem a sorrendjét: izzó = ötlet · naptár = beütemezve ·
   * rakéta = fut a munka · pipa = kész · pillanat-állj = szüneteltetve · tiltó jel = elvetve.
   */
  productUpdateStage: {
    idea: v('outline', LightBulbIcon),
    planned: v('outline', CalendarDaysIcon),
    in_progress: v('outline', RocketLaunchIcon),
    shipped: v('outline', CheckCircleIcon),
    on_hold: v('outline', PauseCircleIcon),
    cancelled: v('outline', NoSymbolIcon),
  },
  /**
   * Fejlesztés PUBLIKÁCIÓS státusza — ikonos jel: a megafon a kihirdetés (ugyanaz a jel, mint a sor
   * „Publikálás" akcióján), a ceruza a még szerkesztés alatti piszkozat, az archívum-doboz a kivezetett
   * bejegyzés. A kártyán CSAK a `draft` jelenik meg belőle: a „kihirdetve" a normál eset, jelvényként zaj.
   */
  productUpdateStatus: {
    draft: v('warning', PencilSquareSolidIcon),
    published: v('success', MegaphoneSolidIcon),
    archived: v('secondary', ArchiveBoxSolidIcon),
  },
  /**
   * Fejlesztés láthatósága — a CÍM ALATT áll, a típus-jelvény mellett, és MINDIG a mező értékét mutatja, nem
   * csak kihirdetett tételen: a `visibility` és a `status` független dimenzió (egy piszkozat is lehet már
   * partneresnek szánt — a szerződés is így mondja). Hogy a jelvény ne ígérjen élő láthatóságot, a mellette
   * álló „piszkozat" jel minősíti. A zár/földgömb a bevett privát–publikus jelentés-pár.
   */
  productUpdateVisibility: {
    public: v('success', GlobeAltSolidIcon),
    // A `privát` KERETES, nem `secondary`: a `secondary` bájtra ugyanaz az osztály, mint a `default`, és a
    // `productUpdateType.performance` is azt viszi — egy `performance` + `internal` tételen a cím alatt két
    // megkülönböztethetetlen szürke pill állt volna egymás mellett (mérve: `ui/badge.tsx` variant-térkép).
    // A súly-aszimmetria szándékos: a PUBLIKUS a figyelemre méltó állapot, a privát a csendes alapeset.
    internal: v('outline', LockClosedSolidIcon),
  },
  /**
   * Fejlesztés időhorizontja a partner-portálon. Vonalas ikon-család (osztályozó jel), és az IKON itt is a
   * HELYET szolgálja ki: a partner-board oszlop-fejlécei ebből olvassák a jelet, ugyanúgy, ahogy a belső
   * board a `productUpdateStage`-ből. Villám = most fut · előre-nyíl = következik · óra = később.
   */
  productUpdateHorizon: {
    now: v('outline', BoltIcon),
    next: v('outline', ForwardIcon),
    later: v('outline', ClockIcon),
  },
  /**
   * A KIADÁS forrása — és egyben a szerkeszthetőség jele. A NAV-sor az adóhatóság rekordja (az összege, a
   * szállítója és a számlaszáma nem írható át), a kézi tétel mindenestül a miénk. Az ikon a különbséget
   * mondja: hivatalos pecsét = NAV-adatszolgáltatás · ceruza = kézzel rögzített.
   */
  expenseSource: {
    nav: v('info', BuildingLibraryIcon),
    manual: v('secondary', PencilSquareIcon),
  },
  /**
   * MELYIK CÉGÜNK kapta a bejövő számlát. Két cég két KÜLÖN adózó: a NAV-adatszolgáltatás adózónként megy,
   * és a költség is két külön könyvelésbe tartozik — a soron tehát látszania kell, melyikébe.
   *
   * SEMLEGES tónus mindkettőre: ez besorolás, nem állapot. Egy „jó/rossz" színezés azt sugallná, hogy az
   * egyik cég sora figyelmet kér.
   */
  /**
   * A SZÁMLA kibocsátója (számlatömb). A `none` NEM hiba (user-döntés): a Számlázz.hu előtag nélküli számlatömbje,
   * ezért ugyanaz a semleges jelvény, mint a MOR / MDIG — a címkéje „—".
   */
  invoiceIssuer: {
    MOR: v('secondary', BuildingOffice2Icon),
    MDIG: v('secondary', BuildingOffice2Icon),
    none: v('secondary', BuildingOffice2Icon),
  },
  expenseIssuer: {
    MOR: v('secondary', BuildingOffice2Icon),
    MDIG: v('secondary', BuildingOffice2Icon),
  },
  /**
   * NAV-bizonylat művelete: a módosító és a sztornó ÖNÁLLÓ számlaként érkezik, az eredetire hivatkozva —
   * ezért a havi összeg nem egyszerű összeadás, és a sornak látszania kell, melyik fajta. A `CREATE` a
   * normál eset, jelvényként zaj lenne: a felület CSAK a másik kettőt mutatja.
   */
  navOperation: {
    MODIFY: v('warning', PencilSquareIcon),
    STORNO: v('danger', NoSymbolIcon),
  },
  /**
   * SZTORNÓZOTT kiadás-sor: ez a bizonylat NEM sztornó, hanem a sztornó CÉLPONTJA — létezik rá egy
   * semmissé tevő bizonylat (a BFF a párosításból vezeti le, nem tárolt mező). Külön fogalom a
   * `navOperation.STORNO`-tól: azzal jelölve a lista azt állítaná, hogy maga a sor a sztornó bizonylat.
   *
   * ⚠️ A TÓNUS SZÁNDÉKOSAN ELTÉR a `navOperation.STORNO` pirosától (user-lelet: egymás mellett alig volt
   * megkülönböztethető). A két fogalom viszonya ugyanaz, mint a számla-oldalon a `storno` (piros, a MŰVELET)
   * és a `cancelled` (szürke, az ÁLLAPOT) páros — ezért ugyanazt a párt vesszük át: ez itt ÁLLAPOT, a sor
   * kikerült a játékból, és nincs vele teendő. A figyelmeztető sárga félrevezetne.
   */
  expenseStorno: {
    stornoed: v('muted', XCircleSolidIcon),
  },
  /**
   * Kampány SZÁMÍTOTT állapota (a BFF a tól–ig dátumból vezeti le, nem tárolt mező). A `running` kapja a
   * hangsúlyt: az idősávon és a listán is ez az, amire ma figyelni kell.
   */
  campaignStatus: {
    planned: v('outline', ClockSolidIcon),
    running: v('success', PlaySolidIcon),
    closed: v('secondary', LockClosedSolidIcon),
  },
  /** Kétlépcsős azonosítás állapota a profilon. */
  twoFactorStatus: {
    on: v('success', ShieldCheckIcon),
    pending: v('warning', ClockIcon),
    off: v('muted', ShieldExclamationIcon),
  },
  /** Szerep forrása a jog-konzolon: manifest (kód) / seed (alap-szerep, szerkeszthető) / konzol (kézi). */
  roleSource: {
    manifest: v('muted', CodeBracketIcon),
    seed: v('info', StarIcon),
    console: v('violet', PencilSquareIcon),
  },
  /** Jog-hatás egy szerepen: adott · tiltott · örökölt. */
  permissionEffect: {
    grant: v('success', PlusCircleIcon),
    deny: v('danger', NoSymbolIcon),
    inherited: v('muted', ArrowTurnDownRightIcon),
  },
  /** Partner-portál szerep egy fiókon (`none` = nincs hozzáférése). */
  portalRole: {
    // UGYANAZ a jel, mint a belső oldal szerep-jelölőié (`marker`): a kivételt jelöljük — a megtekintő ikon
    // nélkül, a felhasználó személy-, az adminisztrátor pajzs-ikont kap; a hozzáférés hiánya mínusz-kör
    'portal-admin': v('outline', ShieldCheckIcon),
    'portal-member': v('outline', UserIcon),
    'portal-viewer': v('outline'),
    none: v('muted', MinusCircleIcon),
  },
  /** A fiók TÍPUSA (iparág) a fiók-listán. */
  accountType: {
    hospitality: v('secondary', CakeIcon),
    accommodation: v('secondary', HomeModernIcon),
    spa: v('secondary', SunIcon),
    other_tourism: v('secondary', MapIcon),
    other_industry: v('secondary', BuildingStorefrontIcon),
  },
  /**
   * JELÖLŐK (flag-ek) — nem enum-értékek, hanem egy sorra tett megjegyzések, amik több felületen ismétlődnek
   * (az „elsődleges" hét helyen). Itt élnek, hogy egy jelölő mindenhol ugyanúgy nézzen ki.
   */
  marker: {
    primary: v('brand', StarIcon),
    // a tömeges importból érkezett kiadás-sor
    imported: v('outline', DocumentArrowUpIcon),
    you: v('brand', UserIcon),
    // PARTNER-PORTÁL SZEREPEK — a badge a TÉNYLEGES szerepet mondja ki, nem egy általános „van hozzáférése"
    // jelölőt (user-döntés). Az ikonok a `role-icons.tsx` elvét követik, hogy a fiók-lap és a jogosultságok
    // oldal ugyanazt a jelet használja ugyanarra a szerepre: a KIVÉTELT jelöljük, nem a normát →
    // a megtekintő (csak olvas) ikon NÉLKÜL áll, a felhasználó SZEMÉLY-ikont kap (részvétel: jegyet nyit,
    // válaszol), az adminisztrátor PAJZSOT (privilégium: ő oszthat hozzáférést a fiókján).
    'portal-viewer': v('outline'),
    'portal-member': v('outline', UserIcon),
    'portal-admin': v('outline', ShieldCheckIcon),
    accessAlways: v('muted', StarIcon),
    viewerOnly: v('muted', EyeIcon),
    accessGranted: v('success', CheckCircleIcon),
    newReply: v('brand', ChatBubbleLeftRightIcon),
    now: v('brand', ClockIcon),
    /** a napló-sor művelete MÁS nevében történt (megszemélyesítés) — figyelem-tónus, mert az aktor nem az, akinek látszik */
    impersonated: v('warning', UserIcon),
    // FÜGGŐ hozzárendelés indulása (a számlázás kezdete) — minden függő sor kap egyet a négy közül; a
    // „hamarosan"/„elmaradt" határt a szerver figyelője mondja meg (`/service-assignments/attention`)
    /** későbbi indulás — tájékoztató */
    pending_start: v('muted', CalendarDaysIcon),
    /** a hozzárendelés induló dátuma az attention-panel ablakán belül van, még `pending` státuszban */
    pending_start_soon: v('warning', ClockIcon),
    /** az induló dátum elmúlt, a sor mégis függő — a generátor nem számlázza */
    pending_start_overdue: v('danger', ExclamationTriangleSolidIcon),
    /** függő sor számlázás-kezdet nélkül — semmi nem jelezné az indulását */
    pending_start_missing: v('danger', QuestionMarkCircleIcon),
  },
} as const satisfies Record<string, Record<string, BadgeVisual>>;

export type BadgeKind = keyof typeof BADGE_CATALOG;

/** Ismeretlen érték jele: semleges tónus + pötty (a hívó feliratával — a felület nem törik, de látszik, hogy nincs térkép). */
export const FALLBACK_BADGE_VISUAL: BadgeVisual = { variant: 'muted' };

/** A katalógus-bejegyzés egy fogalom értékére — `undefined`, ha az érték nincs a térképben. */
export function badgeVisual(kind: BadgeKind, value: string): BadgeVisual | undefined {
  return (BADGE_CATALOG[kind] as Record<string, BadgeVisual>)[value];
}

/**
 * A SAJÁT NEVEK, amelyeket a badge kisbetűsítése NEM érint (user-döntés: „ez nem tárgy vagy általános
 * megnevezés, hanem név, amit így írunk"). Új rendszer- vagy termék-név, ami badge-feliratba kerülhet, IDE kerül.
 */
const PROPER_NAMES = [
  'HotelCMS',
  'ZadirCMS',
  'MailWizz',
  'RoomSome',
  'Zadír',
  'MailR',
  'NAV',
  'Számlázz.hu',
  'Asana',
  'Google',
  'Discord',
  'ActiveCampaign',
  // a kibocsátó CÉGEK kódja (MORGENS / MORGENS Digital) — cégnév-rövidítés, nem általános megnevezés
  'MOR',
  'MDIG',
] as const;

const escapeRegExp = (v: string): string => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** A név szó-határon (betű nem állhat előtte/utána — a magyar toldalék kötőjeles: „HotelCMS-ben"). */
const PROPER_NAME_RE = new RegExp(
  `(?<!\\p{L})(${PROPER_NAMES.map(escapeRegExp).join('|')})(?!\\p{L})`,
  'giu',
);
/**
 * A badge-felirat a SAJÁT NEVEK megőrzésével: a felirat egésze CSS-sel kisbetűs (`lowercase`), a felismert
 * nevek viszont `normal-case` szakaszba kerülnek, így az eredeti írásmódjukban jelennek meg. A DOM-szöveg NEM
 * változik (a felolvasó és a keresés ugyanazt látja) — csak a megjelenítés. Korábban a `lowercase` a neveket is
 * lecsukta („hotelcms-ben kikapcsolva").
 */
export function badgeLabelParts(label: string): { text: string; name: boolean }[] {
  const parts: { text: string; name: boolean }[] = [];
  let last = 0;
  for (const m of label.matchAll(PROPER_NAME_RE)) {
    const at = m.index;
    if (at > last) parts.push({ text: label.slice(last, at), name: false });
    parts.push({ text: m[0], name: true });
    last = at + m[0].length;
  }
  if (last < label.length) parts.push({ text: label.slice(last), name: false });
  return parts;
}

export interface DomainBadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  kind: BadgeKind;
  /** A nyers érték (enum-kulcs / státusz-kód) — a katalógus ebből olvassa a jelet. */
  value: string;
  /** A lokalizált felirat — a hívó adja (kisbetűsítve jelenik meg — a saját nevek kivételével, l. `badgeLabelParts`). */
  label: string;
}

export function DomainBadge({
  kind,
  value,
  label,
  className,
  ...rest
}: DomainBadgeProps): JSX.Element {
  const visual = badgeVisual(kind, value);
  const Icon = visual?.icon;
  return (
    <Badge
      variant={(visual ?? FALLBACK_BADGE_VISUAL).variant}
      icon={Icon ? <Icon /> : undefined}
      dot={visual === undefined}
      className={cn('lowercase', className)}
      data-badge-kind={kind}
      data-badge-value={value}
      {...rest}
    >
      {badgeLabelParts(label).map((part, i) =>
        part.name ? (
          <span key={i} className="normal-case">
            {part.text}
          </span>
        ) : (
          part.text
        ),
      )}
    </Badge>
  );
}
